// Portal del cliente sobre TanStack Query. Rutas PÚBLICAS: no dependen de la
// sesión de `useAuth`, igual que `useAceptacionPublica` y `useInvitacion`.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Comuna } from "@/lib/comunas";
import { apiFetch } from "@/lib/api";
import {
  eliminarArchivoRequest,
  fetchPortal,
  guardarFacturacionRequest,
  subirArchivoRequest,
  type ClaveDeSeccionDelPortal,
  type PayloadFacturacion,
  type Portal,
} from "@/lib/portalPublico";

export const portalKey = (token: string | undefined) => ["portal", token] as const;

export function usePortal(token: string | undefined) {
  return useQuery({
    queryKey: portalKey(token),
    queryFn: () => fetchPortal(token!),
    enabled: Boolean(token),
    // 404 y 410 son estados VÁLIDOS del enlace —no existe, el encargo se
    // cerró— y cada uno tiene su pantalla. Reintentarlos los repetiría sin
    // cambiar nada y demoraría el mensaje que el cliente necesita leer.
    retry: false,
  });
}

/**
 * Las 346 comunas, por la puerta del portal y no por `/api/comunas`.
 *
 * Aquella vive dentro del grupo autenticado y acá no hay sesión. El maestro es
 * el mismo; lo que cambia es la puerta.
 *
 * Se pide SOLO cuando hace falta (`enabled`), o sea al abrir la sección de
 * facturación: el cliente que entra a dejar papeles no tiene por qué bajarse
 * 25 KB de comunas que no va a mirar.
 */
export function useComunasDelPortal(token: string | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: ["portal-comunas", token] as const,
    queryFn: async () => {
      const respuesta = await apiFetch<{ data: Comuna[] }>(
        `/api/portal/${encodeURIComponent(token!)}/comunas`,
      );
      return respuesta.data;
    },
    enabled: Boolean(token) && habilitado,
    // No cambia salvo por decreto: no hay motivo para volver a pedirlas
    // mientras el cliente tenga la pestaña abierta.
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Las tres escrituras SIEMBRAN la cache con lo que devuelve el servidor, en vez
 * de invalidar y volver a pedir.
 *
 * Los tres endpoints responden el portal COMPLETO justamente para esto: la
 * pantalla queda al día sin un segundo viaje. Con `invalidateQueries` habría un
 * parpadeo entre la subida y la lista actualizada, que en una pantalla donde el
 * cliente sube cinco archivos seguidos se nota.
 */
function useEscrituraDelPortal<TVariables>(
  token: string | undefined,
  mutationFn: (variables: TVariables) => Promise<Portal>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (portal) => cliente.setQueryData(portalKey(token), portal),
  });
}

export function useSubirArchivo(token: string | undefined) {
  return useEscrituraDelPortal(token, ({
    seccion,
    archivo,
  }: {
    seccion: ClaveDeSeccionDelPortal;
    archivo: File;
  }) => subirArchivoRequest(token!, seccion, archivo));
}

export function useEliminarArchivo(token: string | undefined) {
  return useEscrituraDelPortal(token, (archivoId: number) =>
    eliminarArchivoRequest(token!, archivoId),
  );
}

export function useGuardarFacturacion(token: string | undefined) {
  return useEscrituraDelPortal(token, (payload: PayloadFacturacion) =>
    guardarFacturacionRequest(token!, payload),
  );
}
